import './PaymentStep.scss';
import React, { useEffect, useRef, useState } from 'react';
import Color from 'color';
import moment from 'moment';
import 'moment-timezone';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { includes, some } from 'lodash';
import { CheckCircleOutline } from '@mui/icons-material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { OrderPageTextField } from '../themedComponents/OrderPageTextField';
import { OrderPageButton } from '../themedComponents/OrderPageButton';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { customerFacingPriceFormatter } from '../utils';
import { calculatePrice } from '../OrderUtils';
import PaymentIframe from './PaymentIframe';
import {
  AccountData,
  ApplyCodeExceptionResponse,
  CodeExceptionStatus,
  CodesService,
  GetPaymentFormRequestData,
  PaymentFormType,
  PaymentsService,
  Tip,
  TipType,
} from '../../api';
import { TipComponent } from './payments/TipComponent';
import RotatingLoader from '../../common/ui/RotatingLoader';
import { OrderPageIntent } from '../OrderPageIntent';
import { OrderFinalDetailsTable } from './OrderFinalDetailsTable';
import { useLanguage } from '../../common/GeneralUtils';
import { OrderPageSelect } from '../themedComponents/OrderPageSelect';
import { FormStyledMenuItem } from './personalDetails/RenderDropDownDetail';

const PaymentStep = ({
  orderPageIntent,
  sessionTtlMilliseconds,
}: {
  orderPageIntent: OrderPageIntent;
  sessionTtlMilliseconds: number | null;
}) => {
  const theme = useTheme();
  const language = useLanguage();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { orderPageState, setTip } = useOrderPage();

  const account = orderPageState.account as AccountData;

  const { t } = useTranslation();
  const [isLoadingApplyCode, setIsLoadingApplyCode] = useState(false);
  const [shouldShowVoucherSuccess, setShouldShowCodeApplySuccess] = useState(false);
  const [voucherOrDiscountCode, setVoucherOrDiscountCode] = useState('');
  const [voucherError, setCodeApplyError] = useState<string | null>(null);
  const [loadingPaymentLink, setLoadingPaymentLink] = useState(false);
  const shouldEnablePaymentsSelection =
    account.payment_integration_settings?.max_payments &&
    account.payment_integration_settings?.max_payments >= 0 &&
    account.payment_integration_settings?.min_amount_for_payments &&
    account.payment_integration_settings?.min_amount_for_payments <= orderPageState.details.price;
  const [shouldShowPaymentsSelector, setShouldShowPaymentsSelector] = useState(shouldEnablePaymentsSelection);
  const [selectedMaxPayments, setSelectedMaxPayments] = useState(1);
  const navigate = useNavigate();
  const shouldShowDuration = orderPageState.orderPage.show_duration;
  const [paymentUrl, setPaymentUrl] = useState(orderPageState?.paymentUrl);
  const showPymentButton = useFeatureFlagEnabled('show-payment-button');

  const shouldShowButtonForPayment =
    (paymentUrl?.includes('meshulam') && isSmallScreen && !shouldShowPaymentsSelector) || showPymentButton;

  const stripeAccountId = orderPageState.account.stripe_connect?.stripe_account_id;

  const depositPrice = orderPageState.account.deposit_percentage
    ? calculatePrice(
        orderPageState.orderPage,
        orderPageState.details,
        orderPageState.dateAndTimeDetails,
        undefined,
        orderPageState.account.deposit_percentage,
      )
    : null;

  const getPaymentForm = async (openPaymentLinkInNewWindow?: boolean) => {
    setLoadingPaymentLink(true);
    // component only used in the us for refetching with tip
    const payload: GetPaymentFormRequestData = {
      language,
      account_id: orderPageState.account.id,
      experience_id: orderPageState.experience.id,
      order_page_id: orderPageState.orderPage.id,
      entity_id: orderPageState.order_id,
      payment_form_type:
        orderPageIntent === OrderPageIntent.GIFT_CARD ? PaymentFormType.VOUCHER : PaymentFormType.ORDER,
    };
    if (orderPageState?.tip?.value && orderPageState?.tip?.value > 0) {
      payload.tip = orderPageState?.tip;
    }
    if (selectedMaxPayments > 1) {
      payload.max_payments = selectedMaxPayments;
    }

    const url = await PaymentsService.getPaymentForm(payload);

    setPaymentUrl(url);
    setLoadingPaymentLink(false);
    if (openPaymentLinkInNewWindow) {
      window.location.href = url;
    }
  };
  useEffect(() => {
    if (!orderPageState?.tip) {
      return;
    }
    void getPaymentForm();
  }, [orderPageState?.tip]);

  const [discountedPrice, setDiscountedPrice] = useState(orderPageState?.details.price);

  const [dynamicSessionTtlMilliseconds, setDynamicSessionTtlMilliseconds] = useState<number | null>(null);
  const dynamicSessionTtlMillisecondsIntervalRef = useRef<number>();
  useEffect(() => {
    if (sessionTtlMilliseconds === null) {
      setDynamicSessionTtlMilliseconds(null);
      return;
    }

    setDynamicSessionTtlMilliseconds(sessionTtlMilliseconds);
    if (sessionTtlMilliseconds <= 0) {
      return;
    }

    dynamicSessionTtlMillisecondsIntervalRef.current = setInterval(() => {
      setDynamicSessionTtlMilliseconds((currentValue) => {
        if (currentValue === null) {
          clearInterval(dynamicSessionTtlMillisecondsIntervalRef.current);
          return null;
        }

        if (currentValue <= 1000) {
          clearInterval(dynamicSessionTtlMillisecondsIntervalRef.current);
          return 0;
        }

        return currentValue - 1000;
      });
    }, 1000) as unknown as number;

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(dynamicSessionTtlMillisecondsIntervalRef.current);
    };
  }, [sessionTtlMilliseconds]);

  if (orderPageState.details.price === 0) {
    window.parent.location.href = `/order/completed?account_id=${orderPageState.account.id}&order_page_id=${orderPageState.orderPage.id}`;
    return <Grid />;
  }
  const submitCode = async () => {
    setShouldShowCodeApplySuccess(false);
    setIsLoadingApplyCode(true);
    setCodeApplyError(null);

    try {
      const resp = await CodesService.applyCode({
        entity_id: orderPageState.order_id,
        code: voucherOrDiscountCode,
      });
      if (resp.payment_link) {
        setPaymentUrl(resp.payment_link);
      }
      if (resp.new_price === 0) {
        navigate(
          `/order/completed?account_id=${orderPageState.account.id}&order_page_id=${orderPageState.orderPage.id}`,
        );
      }
      if (resp.new_price) {
        setDiscountedPrice(resp.new_price);
      }
      setShouldShowCodeApplySuccess(true);
      setIsLoadingApplyCode(false);
    } catch (e: any) {
      if (e.status === 400) {
        const error = e.body as ApplyCodeExceptionResponse;
        let errorstr = 'err';
        switch (error.code) {
          case CodeExceptionStatus.VOUCHER_STATUS_ERROR:
            errorstr = t('discounts.errors.notActive');
            break;
          case CodeExceptionStatus.ENTITY_NOT_FOUND:
            errorstr = t('vouchererrors.entitynotfound');
            break;
          case CodeExceptionStatus.CODE_NOT_FOUND:
            errorstr = t('vouchererrors.notfound');
            break;
          case CodeExceptionStatus.ORDER_NOT_MATCH_VOUCHER:
            errorstr = t('vouchererrors.missmatch');
            break;
          case CodeExceptionStatus.VOUCHER_TOO_OLD:
            errorstr = t('vouchers.expired');
            break;
          case CodeExceptionStatus.VOUCHER_USED:
            errorstr = t('vouchererrors.used');
            break;
          case CodeExceptionStatus.DISCOUNT_NOT_ACTIVE:
            errorstr = t('vouchererrors.discountNotActive');
            break;
          case CodeExceptionStatus.RETURNING_CUSTOMER_TOO_LATE:
            errorstr = t('vouchererrors.returningCustomerTooLate');
            break;
          case CodeExceptionStatus.DISCOUNT_USAGE_LIMIT_EXCEEDED:
            errorstr = t('vouchererrors.usageLimitExceeded');
            break;
          case CodeExceptionStatus.DISCOUNT_ORDER_PAGE_NOT_VALID:
            errorstr = t('vouchererrors.orderPageNotValid');
            break;
          case CodeExceptionStatus.ORDER_DATE_BEFORE_REDEMPTION_PERIOD:
            errorstr = t('vouchererrors.orderDateBeforeRedemptionPeriod');
            break;
          case CodeExceptionStatus.ORDER_DATE_AFTER_REDEMPTION_PERIOD:
            errorstr = t('vouchererrors.orderDateAfterRedemptionPeriod');
            break;
          case CodeExceptionStatus.DISCOUNT_NOT_VALID_FOR_ORDERS:
            errorstr = t('vouchererrors.discountNotValidForOrders');
            break;
          case CodeExceptionStatus.DISCOUNT_NOT_VALID_FOR_VOUCHERS:
            errorstr = t('vouchererrors.discountNotValidForVouchers');
            break;
          case CodeExceptionStatus.DISCOUNT_ALREADY_USED:
            errorstr = t('vouchererrors.onlyOncePerEmail');
            break;
          case CodeExceptionStatus.DISCOUNT_MINIMUM_AMOUNT_NOT_REACHED:
            errorstr = t('vouchererrors.discountMinimumAmountNotReached', { minAmount: error.detail });
            break;
          default:
            setIsLoadingApplyCode(false);
            throw e;
        }
        setIsLoadingApplyCode(false);
        setCodeApplyError(errorstr);
      } else {
        setIsLoadingApplyCode(false);
        throw e;
      }
    }
  };

  const handleVoucherCodeChange = (event: any) => {
    setVoucherOrDiscountCode(event.target.value);
  };
  const shouldShowSandbox =
    paymentUrl && some(['sandbox', 'paymentsdev', 'sc_test', 'elibis'], (substring) => includes(paymentUrl, substring));
  let stripe_integration_publishable_key;

  // new stripe connection
  if (account.stripe_connect) {
    stripe_integration_publishable_key = account.stripe_connect.is_stripe_test_account
      ? import.meta.env.VITE_REACT_APP_TEST_STRIPE_PUBLISHABLE_KEY
      : import.meta.env.VITE_REACT_APP_STRIPE_PUBLISHABLE_KEY;
  }

  function calculateTip(tip?: Tip, price?: number) {
    if (!tip || !price) {
      return 0;
    }
    if (tip.type === TipType.FIXED) {
      return tip.value;
    }
    return (price * tip.value) / 100;
  }

  const paymentTimerDuration =
    dynamicSessionTtlMilliseconds !== null ? moment.duration(dynamicSessionTtlMilliseconds, 'milliseconds') : null;

  return (
    <Box className="payment-step">
      <Grid container alignItems="flex-start" className="payment-step" mb={2}>
        <Grid item container gap={0} mt={isSmallScreen ? undefined : 2}>
          {orderPageState.returningCustomer && (
            <Grid container my={2}>
              <Typography variant="h5" fontWeight={800}>
                {t('order_page.welcomeBack', { name: orderPageState.personalDetails.firstname })}
              </Typography>
            </Grid>
          )}
          {paymentTimerDuration && (
            <Grid item container my={1}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  textAlign: 'center',
                  borderRadius: 1.25,
                  py: 0.25,
                  px: 2,
                  flex: 1,
                  bgcolor: Color(theme.customTheme.palette.text.primary).alpha(0.2).string(),
                }}>
                <Typography variant="body1">
                  {t('paymentTimer.prefix')}{' '}
                  <strong>
                    {t('paymentTimer.timeBold', {
                      timeMinutes: `${paymentTimerDuration.minutes().toString().padStart(2, '0')}:${paymentTimerDuration
                        .seconds()
                        .toString()
                        .padStart(2, '0')}`,
                    })}{' '}
                    {t('duration.min')}{' '}
                  </strong>{' '}
                  {t('paymentTimer.suffix')}
                </Typography>
              </Box>
            </Grid>
          )}
          <OrderFinalDetailsTable
            orderPageIntent={orderPageIntent}
            shouldShowDuration={shouldShowDuration}
            account={orderPageState.account}
            orderPageState={orderPageState}
          />
        </Grid>

        {!orderPageState.account.has_payment_integration && (
          <Grid
            item
            xs={12}
            sm={7}
            justifyContent="center"
            className="price-container"
            sx={{ justifyContent: { xs: 'flex-start' } }}>
            <TableContainer
              sx={{
                border: '1px solid rgba(224, 224, 224, 1)',
                background: 'white',
                borderBottom: '0',
              }}>
              <Table>
                <TableBody>
                  <TableRow sx={{ borderBottom: '0' }}>
                    <TableCell>
                      <OrderPageTypography bold>{t('order_page.total')}</OrderPageTypography>
                    </TableCell>
                    <TableCell sx={{ minWidth: '50px' }}>
                      <OrderPageTypography>
                        {customerFacingPriceFormatter(orderPageState.details.price, orderPageState.account)}
                      </OrderPageTypography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {!orderPageState.account.has_payment_integration && (
          <>
            <Grid item xs={12} className="details-container" sx={{ borderTop: '1px solid #ddd' }}>
              <p>
                {t('order_page.firstname')}
                <strong>{orderPageState.personalDetails.firstname}</strong>
              </p>
              <p>
                {t('order_page.lastname')}
                <strong>{orderPageState.personalDetails.lastname}</strong>
              </p>
              <p>
                {t('order_page.email')} <strong>{orderPageState.personalDetails.email}</strong>
              </p>
              <p>
                {t('order_page.phone')} <strong>{orderPageState.personalDetails.phone}</strong>
              </p>
            </Grid>

            <Alert severity="info" className="payment-footer" sx={{ marginBottom: '32px' }}>
              <div className="payment-footer-text">{t('order_page.payment_in_location')}</div>
            </Alert>
          </>
        )}
      </Grid>
      <Divider style={{ backgroundColor: theme.customTheme.palette.border }} />
      <Grid item container my={2} gap={2}>
        <OrderPageTypography bold variant="h3">
          {t('order_page.price')}
        </OrderPageTypography>
        <OrderPageTypography variant="h3">
          {customerFacingPriceFormatter(orderPageState?.details.price, orderPageState.account, true)}
        </OrderPageTypography>
      </Grid>
      {depositPrice !== null && orderPageIntent === OrderPageIntent.BUY_NOW && (
        <Grid item container my={2} gap={2}>
          <OrderPageTypography bold variant="h3">
            {t('order_page.deposit')}
          </OrderPageTypography>
          <OrderPageTypography variant="h3">
            {customerFacingPriceFormatter(depositPrice, orderPageState.account, true)}
          </OrderPageTypography>
        </Grid>
      )}
      {discountedPrice !== orderPageState?.details.price && (
        <Grid item container my={2} gap={2}>
          <OrderPageTypography variant="h3" color={theme.customTheme.palette.primary} bold>
            {t('order_page.priceAfterDiscount')}
          </OrderPageTypography>
          <OrderPageTypography variant="h3" color={theme.customTheme.palette.primary} bold>
            {customerFacingPriceFormatter(discountedPrice, orderPageState.account, true)}
          </OrderPageTypography>
        </Grid>
      )}
      {orderPageState?.orderPage?.extra_price_component?.tip && (
        <Grid container flexDirection="column" mb={2}>
          {orderPageState?.tip?.value && orderPageState?.tip?.value > 0 && (
            <Grid container item gap={2} mb={2}>
              <OrderPageTypography bold>Gratuity:</OrderPageTypography>
              <OrderPageTypography>
                {customerFacingPriceFormatter(
                  calculateTip(orderPageState?.tip, orderPageState?.details.price),
                  account,
                )}
              </OrderPageTypography>
            </Grid>
          )}
          <TipComponent tipInitial={orderPageState.tip?.value} setTip={setTip} />
        </Grid>
      )}
      <Divider style={{ backgroundColor: theme.customTheme.palette.border }} />
      {includes([OrderPageIntent.BUY_NOW, OrderPageIntent.GIFT_CARD], orderPageIntent) && (
        <Grid container flexDirection="column" my={2}>
          <Grid item mb={2}>
            <OrderPageTypography variant="h3">{t('paymentwithvoucher')}</OrderPageTypography>
          </Grid>
          <Grid item container gap={4} alignItems="center">
            <Grid item xs={isSmallScreen ? 6 : 8}>
              <OrderPageTextField
                size="small"
                fullWidth
                placeholder={t('vouchercode')}
                onChange={handleVoucherCodeChange}
                value={voucherOrDiscountCode}
                error={!!voucherError}
                helperText={voucherError}
              />
            </Grid>
            <OrderPageButton loadingButton variant="outlined" loading={isLoadingApplyCode} onClick={submitCode}>
              <OrderPageTypography>{t('apply')}</OrderPageTypography>
            </OrderPageButton>
            <Grid item hidden={!shouldShowVoucherSuccess}>
              <CheckCircleOutline style={{ color: '#4bbd00' }} />
            </Grid>
          </Grid>
        </Grid>
      )}

      {orderPageState.account.has_payment_integration && (
        <Grid container flexDirection="column">
          <Grid mb={2}>
            <OrderPageTypography variant="h3">{t('paymentwithcard')}</OrderPageTypography>
          </Grid>
          {shouldShowSandbox && <OrderPageTypography color="error">SANDBOX</OrderPageTypography>}
          {loadingPaymentLink || !paymentUrl ? (
            <Grid container height="500px">
              <RotatingLoader />
            </Grid>
          ) : shouldShowPaymentsSelector ? (
            <Grid container my={3} direction="column" alignItems="center" spacing={1}>
              <Grid container my={3} alignItems="center" justifyContent={'center'} spacing={1}>
                <OrderPageTypography variant="h6">{t('order_page.installments')}</OrderPageTypography>
                <Grid item>
                  <OrderPageSelect
                    value={selectedMaxPayments}
                    onChange={(event) => {
                      setSelectedMaxPayments(Number(event.target.value));
                    }}
                    style={{
                      padding: '2px 4px',
                      borderRadius: '4px',
                      border: `1px solid ${theme.customTheme.palette.border}`,
                      width: '100px',
                      color: theme.customTheme.palette.text.secondary,
                      backgroundColor: 'transparent',
                    }}>
                    {Array.from(
                      { length: account.payment_integration_settings?.max_payments || 1 },
                      (_, i) => i + 1,
                    ).map((num) => (
                      <FormStyledMenuItem
                        key={`MenuItem-${num}`}
                        value={num}
                        textColor={theme.customTheme.palette.text.primary}
                        backgroundColor={theme.customTheme.palette.background}>
                        <OrderPageTypography>{num}</OrderPageTypography>
                      </FormStyledMenuItem>
                    ))}
                  </OrderPageSelect>
                </Grid>
              </Grid>
              <Grid item>
                <OrderPageButton
                  onClick={async () => {
                    await getPaymentForm(isSmallScreen);
                    setShouldShowPaymentsSelector(false);
                  }}
                  rel="noopener noreferrer"
                  sx={{
                    height: '50px',
                    width: '200px',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: theme.customTheme.palette.primary,
                  }}>
                  <OrderPageTypography variant={'h3'} bold>
                    {t('open_payment_in_new_tab')}
                  </OrderPageTypography>
                </OrderPageButton>
              </Grid>
            </Grid>
          ) : shouldShowButtonForPayment ? (
            <Grid item>
              <OrderPageButton
                onClick={() => {
                  window.location.href = paymentUrl;
                }}
                rel="noopener noreferrer"
                sx={{
                  height: '50px',
                  width: '200px',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: theme.customTheme.palette.primary,
                }}>
                <OrderPageTypography variant={'h3'} bold>
                  {t('open_payment_in_new_tab')}
                </OrderPageTypography>
              </OrderPageButton>
            </Grid>
          ) : (
            <PaymentIframe
              paymentUrl={paymentUrl}
              stripe_integration_publishable_key={stripe_integration_publishable_key}
              stripeAccountId={stripeAccountId}
              paymentIframeSettings={orderPageState.account?.payment_integration_settings.payment_iframe_settings}
              allowedToShowButton
            />
          )}
        </Grid>
      )}
    </Box>
  );
};

export default PaymentStep;
